<template>
    <ion-app @keydown="catchScanEndCharacter($event)">
        <ion-page>
            <ion-tabs class="custom-tabs-background" :key="languageWatcher">
                <ion-router-outlet :key="testCount" :animated="isApp()" />

                <ion-tab-bar
                    v-if="$route.path.includes(locationId) && !$route.meta?.noTabs && isApp()" slot="bottom"
                    class="button-grey-2 custom-tab-bar"
                >
                    <ion-tab-button tab="home" :href="(locationId) ? `/dashboard/${locationId}/home` : '/home'">
                        <ion-icon :icon="homeOutline" size="small" />
                        <span>{{ $t('object.home') }}</span>
                    </ion-tab-button>

                    <ion-tab-button :disabled="!requiresPermission(PERMISSION_KEY.CHAT_READ)"
                        tab="message" :href="(locationId) ? `/dashboard/${locationId}/chat` : '/home'"
                    >
                        <span class="p-relative">
                            <ion-icon :icon="chatboxOutline" size="small" />
                            <div
                                v-if="chatStore.totalUnread > 0"
                                class="messages-amount coral-coral-background light-light-color center"
                            >
                                {{ (chatStore.totalUnread < 100) ? chatStore.totalUnread : '99+' }}
                            </div>
                        </span>
                        <span>{{ $t('object.message', 2) }}</span>
                    </ion-tab-button>

                    <ion-tab-button
                        tab="list" :href="(locationId) ? `/dashboard/${locationId}/list-overview` : '/home'"
                    >
                        <ion-icon :icon="listOutline" size="small" />
                        <span>{{ $t('object.list', 2) }}</span>
                    </ion-tab-button>

                    <ion-tab-button
                        tab="promotion" :href="(locationId) ? `/dashboard/${locationId}/promotion` : '/home'"
                    >
                        <ion-icon :icon="pricetagsOutline" size="small" class="flipped" />
                        <span>{{ $t('object.promotion', 2) }}</span>
                    </ion-tab-button>

                    <ion-tab-button tab="setting" :href="(locationId) ? `/dashboard/${locationId}/settings` : '/home'">
                        <ion-icon :icon="settingsOutline" size="small" />
                        <span>{{ $t('object.setting') }}</span>
                    </ion-tab-button>
                </ion-tab-bar>
            </ion-tabs>
        </ion-page>

        <div
            v-if="persistentStore.noConnection"
            class="no-connection-wrapper center"
        >
            <div class="no-connection purple-purple-color center fw-b p-1 fs-14">
                <div style="padding-right: 10px;">
                    <ion-icon :src="wifiOutline" size="large"></ion-icon>
                </div>
                <div class="ion-text-center">
                    {{ $t('messages.noConnection1') }}.<br>{{ $t('messages.noConnection2') }}.
                </div>
            </div>
        </div>
    </ion-app>
</template>

<script setup lang="ts">
import { IonApp, IonIcon, IonPage, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs } from '@ionic/vue';
import { computed, onMounted, ref } from 'vue';
import { App } from "@capacitor/app";
import { pingRequest } from "@/api-connectors/basicRequests";
import { set } from 'idb-keyval';
import { route } from "@/router";

import { useUserStore } from "@/stores/user";
import { useLocationStore } from "@/stores/locations";
import { useSupplierStore } from "@/stores/suppliers";
import { useMessagingStore } from "@/stores/messaging";
import { usePersistentStore } from "@/stores/persistent";
import { useListStore } from "@/stores/lists";
import { useNotificationStore } from "@/stores/notification";
import { useChatStore } from "@/stores/chat";

import { useApi } from "@/composables/useApi";

import { setPreference } from "@/functions/preferences";
import { isApp } from "@/functions/enviroment";

import {
    chatboxOutline,
    homeOutline,
    listOutline,
    pricetagsOutline,
    settingsOutline,
    wifiOutline
} from "ionicons/icons";

import { requiresPermission } from './functions/check';
import { PERMISSION_KEY } from './types/user';

const userStore = useUserStore()
const locationStore = useLocationStore()
const supplierStore = useSupplierStore()
const messagingStore = useMessagingStore()
const persistentStore = usePersistentStore()
const notificationStore = useNotificationStore()
const listStore = useListStore()
const chatStore = useChatStore()
const { testCount } = useApi()

const languageWatcher = ref<number>(0)

const locationId = computed<string>(() => String(route.value.params.locationId ?? ''))

// UserStore and Persistent in Preference for better consistency
userStore.$subscribe((mutation, state) => {
    setPreference('userStore', JSON.stringify(state))
})

persistentStore.$subscribe((mutation, state) => {
    setPreference('persistentStore', JSON.stringify(state))
})

// other stores in indexedDB for faster access
locationStore.$subscribe((mutation, state) => {
    set('locationStore', JSON.stringify(state))
    // setPreference('locationStore', JSON.stringify(state))
})
supplierStore.$subscribe((mutation, state) => {
    set('supplierStore', JSON.stringify(state))
})

messagingStore.$subscribe((mutation, state) => {
    set('messagingStore', JSON.stringify(state))
})

listStore.$subscribe((mutation, state) => {
    set('listStore', JSON.stringify(state))
})

notificationStore.$subscribe((mutation, state) => {
    set('notificationStore', JSON.stringify(state))
})

let pingRunning = false
let appInBackground = false

//? catches end-characters (CR, ...) of the scanner, that cause the app to trigger unwanted events
const catchScanEndCharacter = (input: KeyboardEvent) => {
    if (!isApp() || process.env.VUE_APP_DEVELOPMENT_ENV === 'APP') return

    const path = input.composedPath() as HTMLElement[]
    const isChatComponent = path.some((el) => el?.localName === 'vue-advanced-chat')
    if (isChatComponent) return

    if (isNaN(parseInt(input.key)) && input.target?.localName !== "input" && input.target?.localName !== "textarea") {
        input.preventDefault()
        input.stopPropagation()
        return
    }
    // scannerStore.scannerResult += input.key
}

const pingServer = async (): Promise<void> => {
    if (pingRunning) return
    pingRunning = true
    let ping = true
    ping = await pingRequest()

    persistentStore.noConnection = !ping;

    pingRunning = false
}

App.addListener('appStateChange', state => {
    if (!state.isActive) return appInBackground = true
    appInBackground = false
})

const setup = async (): Promise<void> => {
    if (!persistentStore.offlineMode) await pingServer()

    setInterval(() => {
        if (!persistentStore.offlineMode && !appInBackground) pingServer()
    }, 5000)
}

onMounted(setup)

window.addEventListener('change-language', () => {
    languageWatcher.value++
})
</script>

<style scoped>
.no-connection {
    width: 95%;
    min-height: 50px;
    background-color: var(--ion-color-warning);
    border-radius: 10px;
    box-shadow: rgba(50, 50, 93, 0.25) 0 50px 100px -20px, rgba(0, 0, 0, 0.3) 0 30px 60px -30px, rgba(10, 37, 64, 0.35) 0 -2px 6px 0 inset;
}

.no-connection-wrapper {
    width: 100%;
    position: absolute;
    top: calc(env(safe-area-inset-top, 0px) + env(titlebar-area-height, 0px) + var(--android-toolbar-offset, 0px) + 10px);
    left: 0;
    z-index: 999999;
}

.messages-amount {
    position: absolute;
    border-radius: 50%;
    height: 14px;
    width: 14px;
    aspect-ratio: 1 / 1;
    top: -5px;
    right: -5px;
    font-size: 10px;
    padding: 2px;
}

@media (min-width: 750px) {
    .no-connection {
        width: 60%;
    }
}
</style>

<style lang="scss">
@use '@/theme/mixin.scss';

.custom-tabs-background > .tabs-inner {
    @include mixin.light {
        background: var(--ion-color-background);
    }

    @include mixin.dark {
        background: var(--ion-color-purple);
    }
}

.custom-tab-bar {
    @include mixin.light {
        --ion-tab-bar-background: var(--ion-color-light);
        --color: var(--ion-color-purple);
        --color-selected: var(--ion-color-coral);
        --background-focused: var(--ion-color-light-shade);
        --border: var(--ion-color-purple);
    }

    @include mixin.dark {
        --ion-tab-bar-background: var(--ion-color-purple);
        --color: var(--ion-color-grey2);
        --color-selected: var(--ion-color-light);
        --background-focused: var(--ion-color-purple-shade);
        --border: var(--ion-color-purple);
    }
}

@media (max-width: 372px) {
    .custom-tab-bar ion-tab-button {
        max-width: 20%;
        font-size: 9px;
    }
}
</style>
